import {
  defaultShouldDehydrateQuery,
  QueryClient,
} from "@tanstack/react-query";
import SuperJSON from "superjson";

/**
 * P9-07 · A 4xx is the server's final answer (not found, unauthorised, bad
 * input) — retrying only wastes round-trips and delays the error. Only network
 * blips and 5xx are worth another attempt. tRPC surfaces the HTTP status on
 * `error.data.httpStatus`.
 */
function isClientError(error: unknown): boolean {
  const status = (error as { data?: { httpStatus?: number } } | null)?.data
    ?.httpStatus;
  return typeof status === "number" && status >= 400 && status < 500;
}

export const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 30 * 1000,
        // P9-07 · Idempotent reads recover from a transient network/5xx error
        // with a short exponential backoff; a 4xx is final, so don't retry it.
        retry: (failureCount, error) =>
          !isClientError(error) && failureCount < 2,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30_000),
      },
      mutations: {
        // P9-07 · Never auto-retry a mutation — it may have side effects that
        // already landed (a double charge / duplicate write). Rollback + toast
        // is the mutation's failure path, driven per call site.
        retry: 0,
      },
      dehydrate: {
        serializeData: SuperJSON.serialize,
        shouldDehydrateQuery: (query) =>
          defaultShouldDehydrateQuery(query) ||
          query.state.status === "pending",
      },
      hydrate: {
        deserializeData: SuperJSON.deserialize,
      },
    },
  });
